import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext'; 
import CheckoutCouponModule from './CheckoutCouponModule.jsx';

const CheckoutForm = () => {
  const navigate = useNavigate();
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('ONLINE'); // 'ONLINE' or 'COD'

  // Shipping Address Form State
  const [shippingAddress, setShippingAddress] = useState({
    fullName: '', 
    phone: '',
    street: '',
    city: '',
    state: '',
    pincode: ''
  });

  // Dynamic Price & Cart States
  const { cart, cartItems, clearCart } = useCart();
  const activeCart = cart || cartItems || [];
  
  const rawTotal = activeCart.reduce((sum, item) => {
    const itemPrice = item.discountPrice || item.price || 0;
    return sum + (itemPrice * (item.quantity || 1));
  }, 0);

  const [discountAmount, setDiscountAmount] = useState(0);
  const [basePayableTotal, setBasePayableTotal] = useState(rawTotal);
  const [appliedCouponCode, setAppliedCouponCode] = useState('');

  // Dynamically calculate the + ₹20 surcharge only when COD is chosen
  const codExtraCharge = selectedPaymentMethod === 'COD' ? 20 : 0;
  const finalPayableTotal = basePayableTotal + codExtraCharge;

  const handleAddressChange = (e) => {
    setShippingAddress({ ...shippingAddress, [e.target.name]: e.target.value });
  };

  // Synchronize state metrics when coupon is processed
  const handleCouponStateUpdate = (savedCash, finalBill, activeCode) => {
    setDiscountAmount(savedCash);
    setBasePayableTotal(finalBill);
    setAppliedCouponCode(activeCode);
  };

  const validateAddressForm = () => {
    if (!activeCart || activeCart.length === 0) {
      setErrorMessage("Cannot checkout an empty shopping bag. Head back to the gallery and add items first!");
      return false;
    }

    if (
      !shippingAddress.street ||
      !shippingAddress.city ||
      !shippingAddress.state ||
      !shippingAddress.pincode ||
      !shippingAddress.fullName ||
      !shippingAddress.phone
    ) {
      setErrorMessage("⚠️ All physical address and recipient properties must be complete to chart logistics delivery paths.");
      return false;
    }

    if (shippingAddress.pincode.trim().length !== 6) {
      setErrorMessage("⚠️ Please enter a valid 6-digit postal pincode.");
      return false;
    }

    return true;
  };

  // 1. Submit Cash on Delivery Order (Includes ₹20 Surcharge)
  const handleCODOrder = async (authToken, userEmail) => {
    try {
      const response = await fetch('https://api.modischekledings.com/api/payment/cod-order', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({ 
          cartItems: activeCart,
          totalAmount: finalPayableTotal,
          shippingAddress: {
            name: shippingAddress.fullName,
            fullName: shippingAddress.fullName,
            phone: shippingAddress.phone,
            address: shippingAddress.street,
            street: shippingAddress.street,
            city: shippingAddress.city,
            state: shippingAddress.state,
            pincode: shippingAddress.pincode,
            email: userEmail
          },
          couponApplied: appliedCouponCode || null,
          discountValue: discountAmount
        }),
      });

      const data = await response.json();

      if (data.success) {
        setPaymentSuccess(true);
        if (clearCart) clearCart();
        setTimeout(() => navigate('/my-orders'), 3500);
      } else {
        setErrorMessage(data.message || 'Failed to place Cash on Delivery order.');
      }
    } catch (err) {
      console.error("COD Checkout error:", err);
      setErrorMessage('Network timeout connecting with order processing servers.');
    } finally {
      setIsProcessing(false);
    }
  };

  // 2. Submit Online Payment via Razorpay
  const handleRazorpayPayment = async (authToken, userEmail) => {
    try {
      // Create Razorpay Order via Backend Gateway
      const response = await fetch('https://api.modischekledings.com/api/payment/create-order', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({ 
          cartItems: activeCart,
          amount: finalPayableTotal 
        }),
      });

      const orderData = await response.json();

      if (!orderData.success) {
        throw new Error(orderData.message || 'Failed initializing secure order session.');
      }

      // Configure Razorpay Window Instance
      const paymentOptions = {
        key: orderData.keyId || process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: orderData.amount,
        currency: 'INR',
        name: 'Modische Kledings',
        description: appliedCouponCode ? `Showroom Order - Code: ${appliedCouponCode}` : 'Showroom Catalogue Checkout',
        image: 'https://images.unsplash.com/photo-1515886657613-9f3515b0c78f?q=80&w=100&auto=format&fit=crop',
        order_id: orderData.orderId,
        
        handler: async function (paymentResponse) {
          try {
            // Verify Payment, Write to MongoDB, and Push to Shiprocket
            const verifyResponse = await fetch('https://api.modischekledings.com/api/payment/verify-payment', {
              method: 'POST',
              headers: { 
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${authToken}`
              },
              body: JSON.stringify({
                razorpay_order_id: paymentResponse.razorpay_order_id,
                razorpay_payment_id: paymentResponse.razorpay_payment_id,
                razorpay_signature: paymentResponse.razorpay_signature,
                cartItems: activeCart, 
                totalAmount: finalPayableTotal,
                shippingAddress: {
                  name: shippingAddress.fullName,
                  fullName: shippingAddress.fullName,
                  phone: shippingAddress.phone,
                  address: shippingAddress.street,
                  street: shippingAddress.street,
                  city: shippingAddress.city,
                  state: shippingAddress.state,
                  pincode: shippingAddress.pincode,
                  email: userEmail
                },
                couponApplied: appliedCouponCode || null,
                discountValue: discountAmount
              }),
            });

            const verificationResult = await verifyResponse.json();

            if (verificationResult.success) {
              setPaymentSuccess(true);
              if (clearCart) clearCart(); 
              setTimeout(() => navigate('/my-orders'), 3500); 
            } else {
              setErrorMessage(verificationResult.message || 'Signature security checkpoint failed.');
            }

          } catch (error) {
            console.error("Verification connection error:", error);
            setErrorMessage('Network timeout connecting with order processing servers.');
          }
        },
        prefill: {
          name: shippingAddress.fullName,
          email: userEmail,
          contact: shippingAddress.phone,
        },
        theme: {
          color: '#2C1E12',
        },
      };

      const razorpayWindowInstance = new window.Razorpay(paymentOptions);
      
      razorpayWindowInstance.on('payment.failed', function (response) {
        setErrorMessage(`Payment failed: ${response.error.description || 'Transaction declined.'}`);
      });

      razorpayWindowInstance.open();

    } catch (err) {
      console.error("Checkout break:", err);
      setErrorMessage(err.message || 'Could not connect to payment gateway.');
    } finally {
      setIsProcessing(false);
    }
  };

  // Main Submit Handler
  const handleProceedOrder = async () => {
    if (!validateAddressForm()) return;

    setIsProcessing(true);
    setErrorMessage('');

    const authToken = localStorage.getItem('modische_token') || localStorage.getItem('token');
    if (!authToken) {
      setErrorMessage("Please sign in to proceed with secure checkout.");
      navigate('/auth');
      return;
    }

    const activeUser = JSON.parse(localStorage.getItem('modische_user') || '{}');
    const userEmail = activeUser.email || 'customer@modischekledings.com';

    if (selectedPaymentMethod === 'COD') {
      await handleCODOrder(authToken, userEmail);
    } else {
      await handleRazorpayPayment(authToken, userEmail);
    }
  };

  if (paymentSuccess) {
    return (
      <div className="min-h-[85vh] flex flex-col items-center justify-center text-center px-6 bg-[#F5F5DC]">
        <div className="w-24 h-24 mb-6 relative">
          <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 52 52">
            <circle className="stroke-[#2C1E12] fill-none" cx="26" cy="26" r="25" strokeWidth="3" style={{ strokeDasharray: 166, strokeDashoffset: 166, animation: 'strokeCircle 0.6s cubic-bezier(0.65, 0, 0.45, 1) forwards' }} />
            <path className="stroke-[#2C1E12] fill-none" strokeWidth="3" strokeLinecap="round" d="M14.1 27.2l7.1 7.2 16.7-16.8" style={{ strokeDasharray: 48, strokeDashoffset: 48, animation: 'strokeCheck 0.3s cubic-bezier(0.65, 0, 0.45, 1) 0.6s forwards' }} />
          </svg>
        </div>
        <h2 className="text-3xl font-serif uppercase tracking-wider text-[#2C1E12] mb-3 animate-fade-in">Order Confirmed 🎉</h2>
        <p className="text-[10px] uppercase tracking-[0.25em] text-[#664229] opacity-80 max-w-sm leading-relaxed">
          {selectedPaymentMethod === 'COD'
            ? 'Your Cash on Delivery order has been placed. Please keep the exact amount ready upon delivery.'
            : 'Transaction authenticated successfully. Generating shipment details and pushing to fulfillment desk...'}
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-[85vh] bg-[#D4C5B9]/30 flex items-center justify-center p-4 pt-32 pb-20 font-sans">
      <div className="w-full max-w-5xl bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col md:flex-row border border-zinc-200">
        
        {/* LEFT COLUMN: BRAND DETAILS PANEL */}
        <div className="w-full md:w-[42%] bg-[#2C1E12] p-8 flex flex-col justify-between relative text-left">
          <div className="space-y-6">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-10 h-10 bg-zinc-800 rounded-xl flex items-center justify-center font-serif text-lg text-[#F5F5DC] font-black">M</div>
              <h3 className="text-lg font-serif font-bold text-[#F5F5DC] tracking-wide">Modische Kledings</h3>
            </div>

            {/* DYNAMIC BREAKDOWN DISPLAY CARD */}
            <div className="bg-[#FAF9F6] text-[#2C1E12] p-6 rounded-xl border border-[#E5D3B3]/20 shadow-inner space-y-2">
              <div className="flex justify-between items-center text-[10px] uppercase font-mono text-zinc-400">
                <span>Bag Subtotal:</span>
                <span className="font-sans font-bold">₹{rawTotal}</span>
              </div>
              
              {discountAmount > 0 && (
                <div className="flex justify-between items-center text-[10px] uppercase font-mono text-green-700 font-bold">
                  <span>Coupon ({appliedCouponCode}):</span>
                  <span>- ₹{discountAmount}</span>
                </div>
              )}

              {selectedPaymentMethod === 'COD' && (
                <div className="flex justify-between items-center text-[10px] uppercase font-mono text-[#664229] font-bold">
                  <span>COD Handling Fee:</span>
                  <span>+ ₹20</span>
                </div>
              )}

              <div className="border-t border-zinc-100 pt-2">
                <p className="text-[10px] font-sans font-bold uppercase tracking-wider text-zinc-400 mb-0.5">Final Payable Amount</p>
                <div className="text-3xl font-serif font-black tracking-tight text-[#2C1E12]">₹{finalPayableTotal}</div>
              </div>
            </div>

            <div className="bg-zinc-900/40 border border-zinc-800 p-4 rounded-xl text-[#E5D3B3] space-y-1">
              <p className="text-[9px] uppercase tracking-wider text-zinc-400 font-mono">Fulfillment Target:</p>
              <p className="font-mono text-xs text-zinc-200 font-bold">{shippingAddress.fullName || 'No Recipient Added'}</p>
              <p className="font-mono text-[11px] text-zinc-400">{shippingAddress.phone || 'No Phone Specified'}</p>
            </div>
          </div>

          <div className="mt-16 opacity-20 transform translate-y-4 pointer-events-none hidden md:block">
            <div className="w-32 h-20 bg-zinc-700 rounded-lg skew-x-12 mb-2" />
            <div className="w-40 h-12 bg-zinc-600 rounded-lg -skew-x-12" />
            <p className="text-[9px] text-zinc-400 font-mono tracking-widest uppercase mt-4">Secured by Razorpay x Shiprocket</p>
          </div>
        </div>

        {/* RIGHT COLUMN: DELIVERY ADDRESS & PAYMENT METHOD INPUT LAYER */}
        <div className="flex-1 bg-white p-8 sm:p-12 flex flex-col justify-between text-left">
          <div>
            <div className="flex justify-between items-center pb-4 border-b border-zinc-100 mb-6">
              <h2 className="text-xl font-serif text-zinc-800 tracking-wide">Shipping & Payment</h2>
              <button onClick={() => navigate('/cart')} className="text-xs text-zinc-400 hover:text-zinc-900 flex items-center space-x-1 transition font-medium bg-transparent border-none cursor-pointer">
                <span>← Back to Bag</span>
              </button>
            </div>

            {errorMessage && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 text-xs rounded-xl flex items-center space-x-2 font-mono">
                <span className="cursor-pointer font-bold" onClick={() => setErrorMessage('')}>✕</span>
                <span>{errorMessage}</span>
              </div>
            )}

            {/* Address Form Inputs */}
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[9px] uppercase tracking-widest text-[#664229] font-bold mb-1.5">Recipient Full Name</label>
                  <input type="text" name="fullName" value={shippingAddress.fullName} onChange={handleAddressChange} placeholder="Full Name"
                    className="w-full p-3 bg-zinc-50 border border-zinc-200 text-xs text-zinc-800 focus:outline-none focus:border-[#2C1E12] rounded-md transition" />
                </div>
                <div>
                  <label className="block text-[9px] uppercase tracking-widest text-[#664229] font-bold mb-1.5">Contact Phone</label>
                  <input type="text" name="phone" value={shippingAddress.phone} onChange={handleAddressChange} placeholder="10-digit Mobile"
                    className="w-full p-3 bg-zinc-50 border border-zinc-200 text-xs text-zinc-800 focus:outline-none focus:border-[#2C1E12] rounded-md transition" />
                </div>
              </div>

              <div>
                <label className="block text-[9px] uppercase tracking-widest text-[#664229] font-bold mb-1.5">Apartment / Suite / Street Address</label>
                <input type="text" name="street" value={shippingAddress.street} onChange={handleAddressChange} placeholder="House/Flat No., Street, Landmark"
                  className="w-full p-3 bg-zinc-50 border border-zinc-200 text-xs text-zinc-800 focus:outline-none focus:border-[#2C1E12] rounded-md transition" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[9px] uppercase tracking-widest text-[#664229] font-bold mb-1.5">City</label>
                  <input type="text" name="city" value={shippingAddress.city} onChange={handleAddressChange} placeholder="Indore"
                    className="w-full p-3 bg-zinc-50 border border-zinc-200 text-xs text-zinc-800 focus:outline-none focus:border-[#2C1E12] rounded-md transition" />
                </div>
                <div>
                  <label className="block text-[9px] uppercase tracking-widest text-[#664229] font-bold mb-1.5">State</label>
                  <input type="text" name="state" value={shippingAddress.state} onChange={handleAddressChange} placeholder="Madhya Pradesh"
                    className="w-full p-3 bg-zinc-50 border border-zinc-200 text-xs text-zinc-800 focus:outline-none focus:border-[#2C1E12] rounded-md transition" />
                </div>
              </div>

              <div>
                <label className="block text-[9px] uppercase tracking-widest text-[#664229] font-bold mb-1.5">Pincode / Postal Code (6 Digits)</label>
                <input type="text" maxLength="6" name="pincode" value={shippingAddress.pincode} onChange={handleAddressChange} placeholder="452001"
                  className="w-full p-3 bg-zinc-50 border border-zinc-200 text-xs font-mono tracking-widest text-zinc-800 focus:outline-none focus:border-[#2C1E12] rounded-md transition" />
              </div>
            </div>

            {/* PAYMENT METHOD SELECTOR */}
            <div className="mt-6 pt-4 border-t border-zinc-100">
              <label className="block text-[10px] uppercase tracking-widest text-[#664229] font-bold mb-3">Select Payment Method</label>
              <div className="grid grid-cols-2 gap-3">
                <div 
                  onClick={() => setSelectedPaymentMethod('ONLINE')}
                  className={`p-4 rounded-xl border cursor-pointer transition flex flex-col justify-between ${
                    selectedPaymentMethod === 'ONLINE' 
                      ? 'border-[#2C1E12] bg-[#2C1E12]/5 ring-1 ring-[#2C1E12]' 
                      : 'border-zinc-200 hover:border-zinc-300'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-zinc-800">Online Payment</span>
                    <input type="radio" name="paymentType" checked={selectedPaymentMethod === 'ONLINE'} readOnly className="accent-[#2C1E12]" />
                  </div>
                  <span className="text-[10px] text-zinc-500 mt-1">UPI, QR Code, Cards, NetBanking</span>
                </div>

                <div 
                  onClick={() => setSelectedPaymentMethod('COD')}
                  className={`p-4 rounded-xl border cursor-pointer transition flex flex-col justify-between ${
                    selectedPaymentMethod === 'COD' 
                      ? 'border-[#2C1E12] bg-[#2C1E12]/5 ring-1 ring-[#2C1E12]' 
                      : 'border-zinc-200 hover:border-zinc-300'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-zinc-800">Cash on Delivery</span>
                    <input type="radio" name="paymentType" checked={selectedPaymentMethod === 'COD'} readOnly className="accent-[#2C1E12]" />
                  </div>
                  <span className="text-[10px] font-semibold text-[#664229] mt-1">+ ₹20 COD Handling Fee</span>
                </div>
              </div>
            </div>

            {/* PROMO VOUCHERS DRAWER */}
            <div className="mt-4">
              <CheckoutCouponModule 
                cartTotal={rawTotal} 
                onCouponApplied={handleCouponStateUpdate} 
              />
            </div>
          </div>

          <div className="pt-6 border-t border-zinc-100 mt-6">
            <button
              onClick={handleProceedOrder}
              disabled={isProcessing}
              className="w-full py-4 bg-[#2C1E12] text-[#F5F5DC] rounded-xl text-xs font-bold uppercase tracking-widest shadow-xl hover:opacity-90 transition-all disabled:opacity-40 cursor-pointer border-none"
            >
              {isProcessing 
                ? 'Processing...' 
                : selectedPaymentMethod === 'COD' 
                  ? `Place COD Order (₹${finalPayableTotal})` 
                  : `Proceed to Secure Payment (₹${finalPayableTotal})`}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};

export default CheckoutForm;